import { Fragment, useEffect, useState } from 'react';
import { RefreshCw, ChevronRight, ChevronDown, AlertTriangle } from 'lucide-react';
import {
  fetchUtilityRequests,
  groupUtilityRequests,
  setUtilityRequestStatus,
} from '../../lib/adminQueries';
import type { UtilityDemandGroup, UtilityRequestStatus } from '../../types/admin';

const STATUSES: UtilityRequestStatus[] = ['new', 'contacted', 'supported', 'declined'];

const COLUMNS = [
  'Utility',
  'Requests',
  'Contractors',
  'Total annual jobs',
  'Contacts offered',
  'Latest request',
  'Status',
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export default function AdminUtilityDemand() {
  const [groups, setGroups] = useState<UtilityDemandGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      setGroups(groupUtilityRequests(await fetchUtilityRequests()));
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  function toggleRow(key: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function changeStatus(group: UtilityDemandGroup, status: UtilityRequestStatus) {
    setSavingKey(group.key);
    setError(null);
    // Optimistic — the whole group moves together, one status per utility.
    setGroups(prev => prev.map(g => (g.key === group.key ? { ...g, status } : g)));
    try {
      await setUtilityRequestStatus(group.requests.map(r => r.id), status);
    } catch (e) {
      setError(String(e));
      setGroups(prev => prev.map(g => (g.key === group.key ? { ...g, status: group.status } : g)));
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Utility Demand</h2>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-sm text-red-700">{error}</div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading utility demand…</div>
      ) : groups.length === 0 ? (
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-6 text-center text-sm text-gray-500">
          No utility support requests yet. Requests appear here when a contractor selects an
          unsupported utility and asks TrueLoad to work with them.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3" />
                  {COLUMNS.map(h => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left font-medium text-gray-500 uppercase tracking-wide text-xs whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {groups.map(g => {
                  const isOpen = expanded.has(g.key);
                  return (
                    <Fragment key={g.key}>
                      <tr className={`hover:bg-gray-50 ${g.isUnlisted ? 'bg-amber-50' : ''}`}>
                        <td className="px-2 py-3 align-top">
                          <button
                            onClick={() => toggleRow(g.key)}
                            aria-label={isOpen ? 'Collapse requests' : 'Expand requests'}
                            className="p-1 text-gray-400 hover:text-gray-700 rounded"
                          >
                            {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{g.utilityName}</span>
                            {g.isUnlisted && (
                              <span
                                title="Not in the utilities table yet"
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium whitespace-nowrap"
                              >
                                <AlertTriangle className="w-3 h-3" /> Not listed
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-900 font-semibold">{g.requestCount}</td>
                        <td className="px-4 py-3 text-gray-700">{g.contractorCount}</td>
                        <td className="px-4 py-3 text-gray-700">{g.totalAnnualJobs}</td>
                        <td className="px-4 py-3 text-gray-700">{g.contactsOffered}</td>
                        <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                          {formatDate(g.latestRequestAt)}
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={g.status}
                            disabled={savingKey === g.key}
                            onChange={e => changeStatus(g, e.target.value as UtilityRequestStatus)}
                            className="px-2 py-1 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          >
                            {STATUSES.map(s => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        </td>
                      </tr>

                      {isOpen && (
                        <tr className="bg-gray-50">
                          <td colSpan={COLUMNS.length + 1} className="px-4 py-4">
                            <table className="min-w-full text-xs">
                              <thead>
                                <tr className="text-gray-500 uppercase tracking-wide">
                                  {['Contractor', 'City', 'State', 'ZIP', 'Annual jobs', 'Contact note', 'Requested'].map(h => (
                                    <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {g.requests.map(r => (
                                  <tr key={r.id}>
                                    <td className="px-3 py-2 text-gray-900">{r.user_email ?? r.user_id}</td>
                                    <td className="px-3 py-2 text-gray-700">{r.service_city ?? '—'}</td>
                                    <td className="px-3 py-2 text-gray-700">{r.service_state ?? '—'}</td>
                                    <td className="px-3 py-2 text-gray-700">{r.service_zip ?? '—'}</td>
                                    <td className="px-3 py-2 text-gray-700">{r.annual_jobs ?? '—'}</td>
                                    <td className="px-3 py-2 text-gray-700">
                                      {r.has_contact ? (r.contact_note || 'Contact offered') : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                                      {formatDate(r.created_at)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 rounded-b-lg">
            {groups.length} utilit{groups.length !== 1 ? 'ies' : 'y'} requested
          </div>
        </div>
      )}
    </div>
  );
}
